import { useMemo } from "react";
import useMeasure from "@/lib/hooks/useMeasure";
import { filterActiveClients } from "@/lib/utils/client-utils";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { AppBarClientsList } from "./app-bar-clients-list.component";
import { AppBarVendorsList } from "./app-bar-vendors-list.component";

const HEADER_HEIGHT = 30;
const CLIENT_ITEM_H = 30.5;
const VENDOR_ITEM_H = 30.5;
const CLIENT_RATIO = 2 / 3;
const VENDOR_RATIO = 1 / 3;

interface SidebarListsContainerProps {
	expanded: boolean;
	showCode: boolean;
	isClientsCollapsed: boolean;
	isVendorsCollapsed: boolean;
	hideVendors: boolean;
	onClientInteraction: () => void;
	onClientsCollapseChange: (collapsed: boolean) => void;
	onVendorsCollapseChange: (collapsed: boolean) => void;
	onOverflowClick: (type: "clients" | "vendors") => void;
}

function getSlotsUsed(itemCount: number, maxSlots: number): number {
	if (itemCount === 0) return 0;
	if (itemCount <= maxSlots) return itemCount;
	return maxSlots;
}

function distributeItems(
	containerHeight: number,
	clientsCollapsed: boolean,
	vendorsCollapsed: boolean,
	hideVendors: boolean,
	totalClients: number,
	totalVendors: number
): { clientMaxItems: number; vendorMaxItems: number } {
	// Vendors hidden: only the clients header remains, clients take the rest.
	if (hideVendors) {
		const availableForItems = containerHeight - HEADER_HEIGHT;
		if (availableForItems <= 0 || clientsCollapsed) {
			return { clientMaxItems: 0, vendorMaxItems: 0 };
		}
		return {
			clientMaxItems: Math.max(
				1,
				Math.floor(availableForItems / CLIENT_ITEM_H)
			),
			vendorMaxItems: 0,
		};
	}

	// space available once headers are subtracted
	const availableForItems = containerHeight - 2 * HEADER_HEIGHT;
	if (availableForItems <= 0) return { clientMaxItems: 0, vendorMaxItems: 0 };

	if (clientsCollapsed && vendorsCollapsed) {
		return { clientMaxItems: 0, vendorMaxItems: 0 };
	}

	if (clientsCollapsed) {
		return {
			clientMaxItems: 0,
			vendorMaxItems: Math.max(
				1,
				Math.floor(availableForItems / VENDOR_ITEM_H)
			),
		};
	}

	if (vendorsCollapsed) {
		return {
			clientMaxItems: Math.max(
				1,
				Math.floor(availableForItems / CLIENT_ITEM_H)
			),
			vendorMaxItems: 0,
		};
	}

	const totalItems = Math.floor(availableForItems / CLIENT_ITEM_H);
	if (totalItems <= 0) return { clientMaxItems: 1, vendorMaxItems: 1 };

	const idealClients = totalItems * CLIENT_RATIO;
	const idealVendors = totalItems * VENDOR_RATIO;

	let clientMaxItems = Math.floor(idealClients);
	let vendorMaxItems = Math.floor(idealVendors);
	let remaining = totalItems - clientMaxItems - vendorMaxItems;

	if (remaining > 0) {
		const clientFrac = idealClients - clientMaxItems;
		const vendorFrac = idealVendors - vendorMaxItems;
		if (clientFrac >= vendorFrac) {
			clientMaxItems++;
		} else {
			vendorMaxItems++;
		}
	}

	vendorMaxItems = Math.max(1, vendorMaxItems);
	clientMaxItems = Math.max(1, clientMaxItems);

	const vendorSlotsUsed = getSlotsUsed(totalVendors, vendorMaxItems);
	const freedVendorSlots = vendorMaxItems - vendorSlotsUsed;
	if (freedVendorSlots > 0) {
		clientMaxItems = Math.min(
			clientMaxItems + freedVendorSlots,
			totalItems - vendorSlotsUsed
		);
	}

	return {
		clientMaxItems: Math.max(1, clientMaxItems),
		vendorMaxItems,
	};
}

export const SidebarListsContainer: React.FC<SidebarListsContainerProps> = ({
	expanded,
	showCode,
	isClientsCollapsed,
	isVendorsCollapsed,
	hideVendors,
	onClientInteraction,
	onClientsCollapseChange,
	onVendorsCollapseChange,
	onOverflowClick,
}) => {
	const [containerRef, { height: containerHeight }] =
		useMeasure<HTMLDivElement>();

	const totalClients = useCentralStore(
		(state) =>
			filterActiveClients(Object.values(state.clientsLookup)).length
	);
	const totalVendors = useConnectStore(
		(state) => state.connectedVendors.length
	);

	const { clientMaxItems, vendorMaxItems } = useMemo(
		() =>
			containerHeight
				? distributeItems(
						containerHeight,
						isClientsCollapsed,
						isVendorsCollapsed,
						hideVendors,
						totalClients,
						totalVendors
					)
				: { clientMaxItems: 0, vendorMaxItems: 0 },
		[
			containerHeight,
			isClientsCollapsed,
			isVendorsCollapsed,
			hideVendors,
			totalClients,
			totalVendors,
		]
	);

	return (
		<div
			ref={containerRef}
			className="flex-1 min-h-0 w-full flex flex-col"
			data-testid="sidebar-lists-container"
		>
			<AppBarClientsList
				expanded={expanded}
				showCode={showCode}
				maxItems={clientMaxItems}
				onClientInteraction={onClientInteraction}
				onCollapseChange={onClientsCollapseChange}
				onOverflowClick={() => onOverflowClick("clients")}
			/>
			{!hideVendors && (
				<AppBarVendorsList
					expanded={expanded}
					maxItems={vendorMaxItems}
					onVendorInteraction={onClientInteraction}
					onCollapseChange={onVendorsCollapseChange}
					onOverflowClick={() => onOverflowClick("vendors")}
				/>
			)}
		</div>
	);
};
